// districtTree.js — the district tree's state under ✓ / ✕ (B1216, owner approval 2026-09-05). Pure; tested.
//
// The WHOLE state is four explicit sets — include, exclude, own (own-bucket include), ownNot (own-bucket exclude) —
// and the hierarchy. Everything drawn derives from them:
//
//   CHILD_INHERITS_NEAREST_DECIDED_ANCESTOR          a node with no explicit state shows the nearest explicitly
//                                                     decided ancestor's state, drawn as inherited
//   EXPLICIT_CHILD_STATE_OVERRIDES_ANCESTOR_FOR_CHILD_SUBTREE   an explicit state on a node is what it and its
//                                                     subtree show (until a deeper explicit state)
//   EXPLICIT_DESCENDANT_STATES_SURVIVE_A_PARENT_PRESS   a press on a node changes ONLY that node's explicit state; every
//     explicit decision beneath it persists as an exception (owner, 2026-09-06: "when child is ✕ and I select parent, the
//     child should stay ✕"; the search already honours parent ✓ + child ✕ = the parent minus that child). REVERSES the
//     2026-09-05 rule PARENT_PRESS_CLEARS_DESCENDANT_EXCEPTIONS, which used to read: every explicit press on a node clears every explicit state
//                                                     beneath it — no memory of earlier child states
//   NEUTRAL_NEVER_MEANS_EXCLUDED                     under a decided ancestor a press produces the OPPOSITE explicit
//                                                     state (the exception) or removes it; neutral is unreachable
//   PARTIAL_PARENT_IS_DISPLAY_ONLY                   'mixed' = neutral, no decided ancestor, something explicit
//                                                     beneath; the parent itself is not a filter then
//
// The search side of the same model is 0886 (EXPLICIT_DESCENDANT_INCLUDE_OVERRIDES_ANCESTOR_EXCLUDE): an include
// inside an excluded subtree is a carve-out. Chips render explicit decisions only (they read the four arrays).

/** 'include' | 'exclude' | null — the node's own explicit state. */
export function explicitOf(state, code) {
  if (state.include.has(code)) return 'include'
  if (state.exclude.has(code)) return 'exclude'
  return null
}

/** The nearest explicitly decided ANCESTOR's state (not the node itself), or null. */
export function ancestorState(state, code, parentOf) {
  const seen = new Set([code])
  let p = parentOf.get(code)
  while (p && !seen.has(p)) {
    const e = explicitOf(state, p)
    if (e) return e
    seen.add(p); p = parentOf.get(p)
  }
  return null
}

/** What a district row DRAWS: { state: 'include'|'exclude'|'mixed'|null, inherited: boolean }. */
export function districtShown(state, code, parentOf, descendantsOf) {
  const own = explicitOf(state, code)
  if (own) return { state: own, inherited: false }
  const anc = ancestorState(state, code, parentOf)
  if (anc) return { state: anc, inherited: true }
  const beneath = descendantsOf(code).some(c => state.include.has(c) || state.exclude.has(c)) || state.own.has(code) || state.ownNot.has(code)
  return { state: beneath ? 'mixed' : null, inherited: false }
}

/** What the own-bucket row of raion `code` draws: explicit own state, else the raion's shown state, inherited. */
export function districtShownOwn(state, code, parentOf) {
  if (state.own.has(code)) return { state: 'include', inherited: false }
  if (state.ownNot.has(code)) return { state: 'exclude', inherited: false }
  const raion = explicitOf(state, code) || ancestorState(state, code, parentOf)
  return raion ? { state: raion, inherited: true } : { state: null, inherited: false }
}

/** The next EXPLICIT state after a press, given the nearest decided ancestor (or null) and the node's own explicit state. */
export function nextExplicit(ancestor, own) {
  if (!ancestor) return own == null ? 'include' : own === 'include' ? 'exclude' : null   // neutral → ✓ → ✕ → neutral
  if (ancestor === 'include') return own === 'exclude' ? null : 'exclude'                 // inherited ✓ ⇄ exception ✕
  return own === 'include' ? null : 'include'                                             // inherited ✕ ⇄ exception ✓
}

const arr = (set) => [...set]
const without = (set, codes) => arr(set).filter(c => !codes.has(c))

/** A press on a district row → the four arrays to commit. Changes only this node's explicit state; descendants keep theirs. */
export function districtPress(state, code, parentOf, descendantsOf) {
  const next = nextExplicit(ancestorState(state, code, parentOf), explicitOf(state, code))
  const below = new Set([code])   // EXPLICIT_DESCENDANT_STATES_SURVIVE_A_PARENT_PRESS (descendantsOf kept in the signature for callers)
  const include = without(state.include, below), exclude = without(state.exclude, below)
  if (next === 'include') include.push(code)
  if (next === 'exclude') exclude.push(code)
  return { include, exclude, own: without(state.own, below), ownNot: without(state.ownNot, below) }
}

/** A press on the own-bucket row of raion `code`. The own bucket has no descendants; it inherits the raion's shown state. */
export function districtPressOwn(state, code, parentOf) {
  const raion = explicitOf(state, code) || ancestorState(state, code, parentOf)
  const own = state.own.has(code) ? 'include' : state.ownNot.has(code) ? 'exclude' : null
  const next = nextExplicit(raion, own)
  const one = new Set([code])
  const ownArr = without(state.own, one), ownNotArr = without(state.ownNot, one)
  if (next === 'include') ownArr.push(code)
  if (next === 'exclude') ownNotArr.push(code)
  return { include: arr(state.include), exclude: arr(state.exclude), own: ownArr, ownNot: ownNotArr }
}

// ── THE NAME IS NOT THE BOX (owner, 2026-09-06): a press on a district's NAME is select/deselect, never the cycle.
// An explicitly decided row goes back to inherited/neutral (its descendant exceptions stay, as for the box; only its
// own-bucket state clears with it);
// a row showing an INHERITED ✓ becomes an explicit ✕ (that is what "deselect" means under a ✓ parent); anything else
// (neutral, or inherited ✕) becomes an explicit ✓. CHILD_INHERITS_NEAREST_DECIDED_ANCESTOR and
// EXPLICIT_DESCENDANT_STATES_SURVIVE_A_PARENT_PRESS hold unchanged — only which state is written differs from districtPress.
export function districtNamePress(state, code, parentOf, descendantsOf) {
  const own = explicitOf(state, code)
  const next = own ? null : (ancestorState(state, code, parentOf) === 'include' ? 'exclude' : 'include')
  const below = new Set([code])   // EXPLICIT_DESCENDANT_STATES_SURVIVE_A_PARENT_PRESS
  const include = without(state.include, below), exclude = without(state.exclude, below)
  if (next === 'include') include.push(code)
  if (next === 'exclude') exclude.push(code)
  return { include, exclude, own: without(state.own, below), ownNot: without(state.ownNot, below) }
}
/** The same rule for the own-bucket row of raion `code`. */
export function districtNamePressOwn(state, code, parentOf) {
  const cur = state.own.has(code) ? 'include' : state.ownNot.has(code) ? 'exclude' : null
  const shown = cur || explicitOf(state, code) || ancestorState(state, code, parentOf)
  const next = cur ? null : (shown === 'include' ? 'exclude' : 'include')
  const own = arr(state.own).filter(c => c !== code), ownNot = arr(state.ownNot).filter(c => c !== code)
  if (next === 'include') own.push(code)
  if (next === 'exclude') ownNot.push(code)
  return { include: arr(state.include), exclude: arr(state.exclude), own, ownNot }
}
